import React, { useState, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { withStyles } from '@material-ui/core'
import { connect } from 'react-redux'
import { debounce } from 'lodash'
import Button from '@material-ui/core/Button'

import Table from 'uiKit/table/Table'
import TableRow from 'uiKit/table/TableRow'
import TableCell from 'uiKit/table/TableCell'
import Popover from '@material-ui/core/Popover'
import { VerticalThreeDots } from 'uiKit/icons/VerticalThreeDots'
import Modal from 'uiKit/Modal'
import SupervisionDeleteModal from './SupervisionDeleteModal'
import DuplicateSupervision from './DuplicateSupervision'
import Loader from 'uiKit/loaders/loader'
import Search from 'uiKit/Search/Search'
import Pagination from 'uiKit/table/Pagination'
import NoChartData from 'uiKit/NoChartData'
import { getSupervisionTemplates, deleteSupervisionTemplate } from '../../api/supervision'
import { HOME_PATH } from 'configs'
import { isQcsBot } from 'constants/qcs'
import { noSurveySupervisionText } from 'tabs/dashboard/constants/chartsTexts'
import { getBotPackage } from 'tabs/home/api/botPackage'
import { getCachedBotPackageAssignment, hasCachedBotPackageAssignment } from 'tabs/home/helpers/orgPackages'
import {
  isBotPackageSupervisionEnabled,
  isDefaultOnlySupervision,
  isQcsStandardSupervisionTemplate,
} from 'tabs/home/api/usageLimits'

const TableWrap = styled.div`
  margin: 20px 0 20px 0;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`
const ActionSelectorContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
`

const ActionSelectorButton = styled.button`
  background: transparent;
  border: none;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => (props.disabled ? 0.4 : 1)};
`

const PopoverButton = styled.button`
  background: transparent;
  border: none;
  padding: 10px 16px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: 'Lato';
  font-size: 14px;
  color: ${props => (props.hasAttentionStyles ? 'var(--color-text-error)' : 'var(--color-text-primary)')};
  &:hover {
    background-color: var(--color-selago-grey);
  }
`

const DraftTag = styled.span`
  color: var(--color-white);
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  background-color: rgba(97, 79, 128, 1);
  padding: 4px 8px;
  border-radius: 4px;
  margin-left: 8px;
  display: inline-block;
  vertical-align: middle;
  min-width: 62px;
`

const styles = {
  popover: {
    borderRadius: '10px',
    boxShadow: '0px 4px 29px rgba(19, 69, 186, 0.0855129)',
    padding: '8px 0',
    minWidth: '160px',
  },
}

const modalStyle = theme => ({
  customModalPaper: {
    padding: '0px!important',
    height: '246px!important',
    width: '540px!important',
  },
  customModalPaperDuplicate: {
    padding: '0px!important',
    height: '550px!important',
    width: '701px!important',
  },
})

const columnTitles = [
  { title: 'Name', maxWidth: 365, width: '70%' },
  { title: 'Role Name', maxWidth: 150, width: '20%' },
  { title: 'No.of questions', maxWidth: 150, width: '20%' },
  { title: 'Action', maxWidth: 80, width: '10%' },
]

const columnTitlesReadOnly = columnTitles.filter(col => col.title !== 'Action')

const getRowBackground = index => {
  return { background: index % 2 === 0 ? '' : 'var(--color-table-row-primary)' }
}

const ActionSelector = ({ onEdit, onDuplicate, onDelete, session, isDefaultOnly }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const isStandard = isQcsStandardSupervisionTemplate(session)
  // Default package mode: Dynamic templates keep the ⋮ visible but disabled.
  const isMenuDisabled = Boolean(isDefaultOnly && !isStandard)

  const handleClick = event => {
    if (isMenuDisabled) return
    setAnchorEl(event.currentTarget)
    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleAction = action => {
    handleClose()
    if (action === 'edit') onEdit()
    if (action === 'duplicate') onDuplicate()
    if (action === 'delete') onDelete()
  }

  // 4 is the bn bot template. GA supervision (ids 5, 719) is editable but not duplicable.
  const isEdit = session?.id !== 4
  // Default mode: hide Duplicate on QCS Standard Supervision; keep existing id rules otherwise.
  const isDuplicate =
    ![4, 5, 719].includes(session?.id) && !(isDefaultOnly && isStandard)

  return (
    <ActionSelectorContainer>
      <ActionSelectorButton onClick={handleClick} disabled={isMenuDisabled} aria-disabled={isMenuDisabled}>
        <VerticalThreeDots />
      </ActionSelectorButton>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          style: styles.popover,
        }}>
        {isEdit && <PopoverButton onClick={() => handleAction('edit')}>Edit</PopoverButton>}
        {isDuplicate && !session?.isDraft && (
          <PopoverButton onClick={() => handleAction('duplicate')}>Duplicate</PopoverButton>
        )}
        <PopoverButton hasAttentionStyles onClick={() => handleAction('delete')}>
          Delete
        </PopoverButton>
      </Popover>
    </ActionSelectorContainer>
  )
}

const inputSearchStyles = {
  width: 'max-content',
  border: '1px solid var(--color-ghost-grey)',
  display: 'flex',
  padding: '20px',
  minWidth: '340px',
  maxHeight: '45px',
  marginRight: '10px',
  borderRadius: '10px',
  justifyContent: 'space-between',
  height: '45px',
  backgroundColor: 'var(--color-white)',
}

const buttonStyles = {
  backgroundColor: 'var(--color-button-primary)',
  borderRadius: '8px',
  fontSize: '18px',
  fontWeight: '600',
  textTransform: 'none',
  height: 48,
  width: '179px',
  color: 'var(--color-text-on-primary)',
}

const SupervisionTable = ({ classes, activeBot, history }) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [templateId, setTemplateId] = useState(null)
  const [searchingValue, setSearchingValue] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [supervisionEnabled, setSupervisionEnabled] = useState(true)
  const [isDefaultOnly, setIsDefaultOnly] = useState(false)
  // false until package flags are known — avoids Create / Action flashing then hiding
  const [packageReady, setPackageReady] = useState(false)
  const botId = activeBot.id
  const isQcs = isQcsBot(activeBot)

  useEffect(() => {
    if (!activeBot || !activeBot.id) {
      setLoading(false)
      return
    }
    getTemplateLists()
  }, [activeBot, page, rowsPerPage])

  useEffect(() => {
    // CareBrain has no package assignment; a 404 here must not be requested.
    if (!activeBot?.id || !isQcs) {
      setSupervisionEnabled(true)
      setIsDefaultOnly(false)
      setPackageReady(true)
      return undefined
    }

    const applyFlags = assignment => {
      setSupervisionEnabled(isBotPackageSupervisionEnabled(assignment))
      setIsDefaultOnly(isDefaultOnlySupervision(assignment))
      setPackageReady(true)
    }

    // Sync seed when list already warmed the assignment cache (no Create flash).
    if (hasCachedBotPackageAssignment(activeBot.id)) {
      applyFlags(getCachedBotPackageAssignment(activeBot.id))
    } else {
      setPackageReady(false)
    }

    const controller = new AbortController()
    getBotPackage(activeBot.id, controller.signal)
      .then(assignment => {
        if (controller.signal.aborted) return
        applyFlags(assignment)
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setSupervisionEnabled(true)
          setIsDefaultOnly(false)
          setPackageReady(true)
        }
      })
    return () => controller.abort()
  }, [activeBot?.id, isQcs])

  const titles = packageReady && supervisionEnabled ? columnTitles : columnTitlesReadOnly
  const showCreateButton = packageReady && supervisionEnabled && !isDefaultOnly
  const showActionColumn = packageReady && supervisionEnabled

  const getTemplateLists = (searchQuery = searchingValue, pageNum) => {
    setLoading(true)
    getSupervisionTemplates(activeBot.id, {
      search: searchQuery,
      page: pageNum === undefined ? page : pageNum,
      size: rowsPerPage,
    })
      .then(response => {
        if (response && response.data) {
          setTemplates(response.data || [])
          setTotalCount(response.meta.totalElements || 0)
        } else {
          setTemplates([])
          setTotalCount(0)
        }
        setError(null)
        if (searchQuery !== searchingValue) {
          setPage(0)
        }
      })
      .catch(err => {
        console.error('Error fetching supervision templates:', err)
        setError('Failed to load supervision templates')
        setTemplates([])
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleEdit = item => {
    setTemplateId(item.id)
    history.push(`/admin/bot/${activeBot.id}/settings/createSupervision/${item.id}`)
  }

  const handleDuplicate = item => {
    setTemplateId(item.id)
    setShowDuplicateModal(true)
  }

  const handleDelete = item => {
    setShowConfirmModal(true)
    setTemplateId(item.id)
  }

  const handleConfirmToggle = () => {
    deleteSupervisionTemplate(activeBot.id, templateId).finally(() => {
      setShowConfirmModal(false)
      setTemplateId(null)
      setLoading(false)
      setPage(0)
      getTemplateLists('', 0)
    })
  }

  const handleCancelToggle = () => {
    setShowConfirmModal(false)
    setShowDuplicateModal(false)
    setTemplateId(null)
  }

  const debouncedSearch = React.useCallback(
    debounce(value => {
      getTemplateLists(value)
    }, 500),
    [activeBot],
  )

  const onChangeHandler = e => {
    const value = e.target.value
    setSearchingValue(value)
    debouncedSearch(value)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = event => {
    const newRowsPerPage = event.target.value === 'All' ? totalCount : parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
  }

  const handleConfirmDuplicate = () => {
    setShowDuplicateModal(false)
    setTemplateId(null)
    setPage(0)
    getTemplateLists('', 0)
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Loader />
        </div>
      )
    }

    if (error) {
      return <div style={{ textAlign: 'center', color: 'var(--color-text-error)', padding: '40px 0' }}>{error}</div>
    }
    return (
      <>
        {templates.length > 0 ? (
          <Table titles={titles}>
            {templates.map((item, index) => (
              <TableRow key={item.id} style={getRowBackground(index)}>
                <TableCell style={{ maxWidth: titles[0].maxWidth, textAlign: 'left' }}>
                  {item.name} {item.isDraft && <DraftTag>In draft</DraftTag>}
                </TableCell>
                <TableCell style={{ maxWidth: titles[1].maxWidth, textAlign: 'left' }}>Carer / Manager</TableCell>
                <TableCell style={{ maxWidth: titles[2].maxWidth, textAlign: 'left' }}>
                  {item.numberOfQuestions === null ? '-' : item.numberOfQuestions}
                </TableCell>
                {showActionColumn && (
                  <TableCell style={{ maxWidth: titles[3].maxWidth, textAlign: 'left' }}>
                    <ActionSelector
                      onEdit={() => handleEdit(item)}
                      onDuplicate={() => handleDuplicate(item)}
                      onDelete={() => handleDelete(item)}
                      session={item}
                      isDefaultOnly={isDefaultOnly}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </Table>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0' }}>
            <NoChartData text={noSurveySupervisionText} />
          </div>
        )}
        {templates.length > 0 && (
          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              count={totalCount}
              rowsPerPage={rowsPerPage === totalCount ? 'All' : rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              hasSelect={true}
              labelRowsPerPage="Rows per page:"
            />
          </div>
        )}
      </>
    )
  }

  return (
    <TableWrap>
      <FlexContainer>
        <Search value={searchingValue} onChange={onChangeHandler} placeholder="Search" inputStyle={inputSearchStyles} />
        {showCreateButton && (
          <Button
            onClick={() => history.push(`${HOME_PATH}/bot/${botId}/settings/createSupervision`)}
            style={buttonStyles}>
            Create
          </Button>
        )}
      </FlexContainer>
      {renderContent()}
      <Modal
        open={showConfirmModal}
        onClose={handleCancelToggle}
        disableClose={false}
        classes={{ paper: classes.customModalPaper }}>
        <SupervisionDeleteModal onConfirm={handleConfirmToggle} onCancel={handleCancelToggle} />
      </Modal>
      <Modal
        open={showDuplicateModal}
        onClose={handleCancelToggle}
        disableClose={false}
        classes={{ paper: classes.customModalPaperDuplicate }}>
        <DuplicateSupervision
          templateId={templateId}
          onConfirm={handleConfirmDuplicate}
          onCancel={handleCancelToggle}
        />
      </Modal>
    </TableWrap>
  )
}

ActionSelector.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  session: PropTypes.object,
  isDefaultOnly: PropTypes.bool,
}

SupervisionTable.propTypes = {
  classes: PropTypes.object.isRequired,
  activeBot: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
}

const mapStateToProps = state => ({
  activeBot: state.activeBot,
})

// Apply HOCs in the correct order
const StyledComponent = withStyles(modalStyle)(SupervisionTable)
const ConnectedComponent = connect(mapStateToProps)(StyledComponent)
export default withRouter(ConnectedComponent)
